import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquarePlus, Send } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";

const fmtDT = (iso) => (iso || "").replace("T", " ").slice(0, 16);
const fmtD = (iso) => { const s = (iso || "").slice(0, 10); return s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : ""; };

function CommentForm({ bookingId, onAdded }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/calendar/bookings/${bookingId}/comments`, { text });
      onAdded(r.data.comment);
      setText(""); setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  if (!open) return (
    <button type="button" data-testid={`cal-history-add-comment-${bookingId}`} onClick={() => setOpen(true)}
      className="text-[11px] text-gray-500 hover:text-black flex items-center gap-1 mt-1">
      <MessageSquarePlus className="w-3.5 h-3.5" />{t("cal.addComment")}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5 mt-1.5 w-full">
      <input autoFocus value={text} onChange={(e) => setText(e.target.value)} data-testid={`cal-history-comment-input-${bookingId}`}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setOpen(false); }}
        placeholder={t("cal.commentPlaceholder")}
        className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400" />
      <button type="button" disabled={busy || !text.trim()} onClick={submit} data-testid={`cal-history-comment-send-${bookingId}`}
        className="p-1.5 rounded-md bg-black text-white disabled:opacity-40"><Send className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function VisitRow({ h, i, onComment }) {
  return (
    <div data-testid={`cal-history-row-${i}`} className="text-xs py-2 border-b border-gray-100 last:border-0">
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="font-semibold whitespace-nowrap">{fmtDT(h.start_at || h.preferred_date)}</span>
        <span className="font-medium">{h.service}</span>
        {h.staff_name && <span className="text-gray-500">· {h.staff_name}</span>}
        {h.price != null && <span className="text-gray-500">· {h.price} €</span>}
        {h.attendance === "came" && <span>✅</span>}
        {h.attendance === "no_show" && <span>❌</span>}
        {h.status === "cancelled" && <span className="text-gray-400 line-through">✕</span>}
      </div>
      {h.products_used && <div className="text-gray-600 mt-0.5">🧴 {h.products_used}</div>}
      {h.note && <div className="text-gray-700 italic mt-0.5">💬 {h.note}</div>}
      {(h.comments || []).map((c) => (
        <div key={c.id} className="mt-1 pl-2 border-l-2 border-gray-200 text-gray-700" data-testid={`cal-history-comment-${c.id}`}>
          <span className="text-gray-400">{fmtD(c.at)} · {c.author}:</span> {c.text}
        </div>
      ))}
      <CommentForm bookingId={h.id} onAdded={(c) => onComment(h.id, c)} />
    </div>
  );
}

export default function ClientHistoryBlock({ detail, history, setHistory, onShowMore }) {
  const { t } = useTranslation();
  const past = (history?.items || []).filter((h) => h.id !== detail.id);
  const last = past.find((h) => h.status !== "cancelled" && h.attendance !== "no_show") || past[0];
  const mem = history?.memory;
  const onComment = (id, c) => setHistory((hs) => ({
    ...hs, items: hs.items.map((h) => (h.id === id ? { ...h, comments: [...(h.comments || []), c] } : h)) }));
  const canSearch = !!(detail.phone || detail.client_name);
  return (
    <div className="rounded-lg border-2 border-gray-900 bg-gray-50 p-3" data-testid="cal-detail-history">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="font-bold text-sm">👤 {t("cal.history")}{history?.total ? ` · ${past.length ? history.total - 1 : 0}` : ""}</div>
        {mem?.no_shows > 0 && <span className="text-xs font-semibold text-red-600">🚫 {t("cal.memNoShows")}: {mem.no_shows}</span>}
      </div>
      {!canSearch && <div className="text-xs text-gray-400">{t("cal.noHistory")}</div>}
      {canSearch && history && !past.length && <div className="text-xs text-gray-400">{t("cal.newClient")}</div>}
      {last && (
        <div className="text-xs text-gray-800 bg-white border border-gray-200 rounded-md px-2.5 py-2 mb-2 space-y-0.5" data-testid="cal-client-last-visit">
          <div>
            <span className="text-gray-500">{t("cal.lastVisit")}:</span> <b>{fmtD(last.start_at || last.preferred_date)}</b> — {last.service}
            {last.staff_name ? ` ${t("cal.atMaster")} ${last.staff_name}` : ""}
          </div>
          {last.products_used && <div>🧴 {last.products_used}</div>}
          {last.note && <div className="italic">💬 {last.note}</div>}
          {mem?.visits > 0 && (
            <div className="text-gray-500 flex flex-wrap gap-x-3 pt-0.5">
              <span>🔁 {mem.visits} {t("cal.memVisits")}</span>
              {mem.avg_interval_days ? <span>⏱ ~{t("cal.memEvery")} {mem.avg_interval_days} {t("cal.memDays")}</span> : null}
              {mem.favorite_master && <span>⭐ {mem.favorite_master}</span>}
              {mem.avg_check != null && <span>💶 {t("cal.memAvgCheck")}: {mem.avg_check} €</span>}
            </div>
          )}
        </div>
      )}
      {past.map((h, i) => <VisitRow key={h.id || i} h={h} i={i} onComment={onComment} />)}
      {history && !history.expanded && history.total > history.items.length && (
        <button data-testid="cal-history-more" onClick={onShowMore}
          className="text-xs font-bold underline mt-1.5 hover:text-gray-600">{t("cal.showMore")}</button>
      )}
    </div>
  );
}
